/**
 * SIL-1347 - marketplace + listing-card copy. Wording from
 * docs/exchange/design-handoff/README.md section 1 and spec
 * docs/superpowers/specs/2026-09-28-exchange-buyer-ui-design.md section 4.1.
 *
 * Same discipline as `activeVerification.ts` and `buyerVerification.ts`: the
 * sentences live here, the components are layout and behaviour. Nothing in this
 * file restates Active Verification explainer copy or buyer-verification banner
 * copy - those have their own modules and this one imports neither.
 */
import type { ListingType } from '../../../dto/listing.js';
import type { MarketLabel, PaymentTerms } from '../../../dto/marketplace.js';

/* -------------------------------------------------------------------------- */
/* Page chrome                                                                */
/* -------------------------------------------------------------------------- */

/**
 * The page H1 is deliberately NOT here. `structural-checks.test.ts` (SIL-540)
 * parses the route source for a LITERAL `PageHeader title` and requires it to
 * equal the `CUSTOMER_NAV` label for the same path - a constant reference fails
 * that check by construction, and the invariant it protects (the nav and the
 * heading reading the same word for the same page) is worth more than moving one
 * string into this module. The label lives in
 * `components/shell/nav/customerNav.ts`; the route repeats it verbatim.
 */
export const MARKETPLACE_SUBTITLE =
  'Verified AI compute hardware from registered sellers. Every listing carries a Silicon Passport.';

/** The 300px header search input (handoff: "matches ID, model, site and server"). */
export const MARKETPLACE_SEARCH_LABEL = 'Search listings';
export const MARKETPLACE_SEARCH_PLACEHOLDER = 'Search ID, model or server';

export const MARKETPLACE_ERROR_TITLE = 'Could not load the exchange.';

/**
 * SIL-1377 - `MarketplaceResultsBoundary`'s fallback, for a render error INSIDE
 * the results region. Distinct from `MARKETPLACE_ERROR_TITLE` above, which is the
 * failed-REQUEST state: this one only ever shows when the request succeeded and
 * the rows could not be drawn, and the wording is scoped to the listings rather
 * than to "the exchange" because the rest of the page is still standing and
 * usable. Naming the whole surface would be a false statement about what broke.
 */
export const MARKETPLACE_RESULTS_CRASH_TITLE = 'Could not display these listings.';

/**
 * Spec section 4.1, verbatim. Shown whenever the filtered result set is empty -
 * with or without active filters, because "clear the search" is the action in
 * both cases.
 */
export const MARKETPLACE_EMPTY_TITLE = 'No listings match these filters';
export const MARKETPLACE_EMPTY_BODY = 'Adjust your filters or clear the search.';

/* -------------------------------------------------------------------------- */
/* Filter card                                                                */
/* -------------------------------------------------------------------------- */

export const MARKETPLACE_FILTERS_TITLE = 'Filters';
export const MARKETPLACE_FILTERS_CLEAR = 'Clear';
export const MARKETPLACE_FILTER_MODEL = 'GPU model';
export const MARKETPLACE_FILTER_TYPE = 'Listing type';
export const MARKETPLACE_FILTER_CONDITION = 'Condition';
export const MARKETPLACE_FILTER_REGION = 'Region';
/** SIL-1444 - the new asset-type checkbox group (spec Change 3). */
export const MARKETPLACE_FILTER_ASSET_TYPE = 'Asset type';
/** SIL-1444 - renamed from "Price / GPU (USD)"; see LISTING_CARD_PRICE_PER_UNIT. */
export const MARKETPLACE_FILTER_PRICE = 'Price / Unit (USD)';
export const MARKETPLACE_FILTER_PRICE_MIN = 'Min';
export const MARKETPLACE_FILTER_PRICE_MAX = 'Max';
/**
 * A filter group with nothing to offer renders this instead of an empty box:
 * the facet values come from the listings on the page, so "no options" is a
 * fact about the result set, not a loading state.
 */
export const MARKETPLACE_FILTER_NO_OPTIONS = 'No options';

/* -------------------------------------------------------------------------- */
/* Listing card                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Listing-type labels. `listing_type` is the DB enum; these are the handoff's
 * words for it ("Lot / Single node"), used by both the card badge and the
 * filter checkbox so a buyer reads the same label in both places.
 */
export const LISTING_TYPE_LABELS: Record<ListingType, string> = {
  asset: 'Single node',
  lot: 'Lot',
  sku: 'SKU quantity',
};

export const LISTING_CARD_VIEW_DETAILS = 'View details';
/** The verified card's single filled action. An unverified card has none. */
export const LISTING_CARD_BUY_NOW = 'Buy now';
export const LISTING_CARD_VIEW_AUCTION = 'View auction';

/**
 * 2x2 metric grid labels. The "Indicative" spellings are the unverified tier.
 *
 * SIL-1444: renamed from "Price / GPU" -> "Total Price" (the full listing
 * price, every asset type, not just GPU listings) and "Price / node" ->
 * "Price / Unit" (lot: total / asset count; single node or sku quantity of
 * one: equal to Total Price). See marketplaceListingHelpers.ts for the math.
 */
export const LISTING_CARD_TOTAL_PRICE = 'Total Price';
export const LISTING_CARD_INDICATIVE_TOTAL = 'Indicative Total';
export const LISTING_CARD_PRICE_PER_UNIT = 'Price / Unit';
export const LISTING_CARD_INDICATIVE_PER_UNIT = 'Indicative / Unit';
export const LISTING_CARD_LOCATION = 'Location';
// SIL-1480: positive-only marker — renders when `sellerVerifiedOf(listing)`
// is true, nothing otherwise. Never a seller name or id.
export const LISTING_CARD_VERIFIED_SELLER = 'Verified seller';
export const LISTING_CARD_SHIPS = 'Ships';
/** Every "the seller did not state it" cell, so the card never renders a blank. */
export const LISTING_CARD_NOT_STATED = 'Not stated';

export const listingCardShipsWithin = (days: number) => `${days} day${days === 1 ? '' : 's'}`;
export const listingCardNodes = (nodes: number) => `${nodes} node${nodes === 1 ? '' : 's'}`;

export const PAYMENT_TERMS_LABELS: Record<PaymentTerms, string> = {
  escrow: 'Escrow',
  wire_on_acceptance: 'Wire on acceptance',
  net_30: 'Net 30',
};

/* --- Evidence strip, verified tier --------------------------------------- */

export const listingCardNodesPass = (pass: number, nodes: number) =>
  `${pass}/${nodes} nodes Pass`;
export const listingCardObservations = (obs: number) => `${obs} obs.`;
export const listingCardCustodyEvents = (events: number) =>
  `${events} custody event${events === 1 ? '' : 's'}`;

/**
 * The "% vs FMV" item. Spec section 4.1: it is ALWAYS shown on a verified card -
 * the prototype's `showFmvOnCards` tweak is not built - so there is also a
 * sentence for the case where no FMV band resolved, rather than a hidden item.
 */
export const listingCardPctVsFmv = (pct: number) =>
  `${pct >= 0 ? '+' : '-'}${Math.abs(pct).toFixed(1)}% vs FMV`;
export const LISTING_CARD_NO_FMV_BAND = 'No FMV band yet';
/** SIL-2504: the listed asset's SKU match is inferred; no band is shown. */
export const LISTING_CARD_SKU_MATCH_UNVERIFIED = 'Unverified SKU match';

/* --- Evidence strip, unverified tier ------------------------------------- */

export const LISTING_CARD_CUSTODY_UNBROKEN = 'Custody unbroken';
export const LISTING_CARD_CUSTODY_UNKNOWN = 'Custody not established';
/** "Priced below / at / above market" (within 3% of the FMV median = "at"). */
export const MARKET_LABEL_TEXT: Record<MarketLabel, string> = {
  below: 'Priced below market',
  at: 'Priced at market',
  above: 'Priced above market',
};
/**
 * The unverified strip's last line. The lock is the banner's job to resolve -
 * this states the consequence without becoming a second call to action (spec
 * section 4.1: the banner stays the only one).
 */
export const LISTING_CARD_UNLOCK_NOTE = 'Exact price and Passport details after verification';
